import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  calculateSPRSScore,
  calculateSOC2Score,
  calculateISO27001Score,
  calculateHIPAAScore,
  calculateNISTCSFScore,
  calculatePCIDSSScore,
  calculateFedRAMPScore,
} from './data/controlWeights';

// ── Framework metadata ────────────────────────────────────────────────────────
const FRAMEWORK_META = {
  cmmc_l1:  { title: 'System Security Plan',                  subtitle: 'CMMC 2.0 — Level 1 Foundational',                   classification: 'CUI',              shortName: 'CMMC L1'   },
  cmmc_l2:  { title: 'System Security Plan',                  subtitle: 'CMMC 2.0 — Level 2 Advanced',                       classification: 'CUI',              shortName: 'CMMC L2'   },
  cmmc_l3:  { title: 'System Security Plan',                  subtitle: 'CMMC 2.0 — Level 3 Expert',                         classification: 'CUI',              shortName: 'CMMC L3'   },
  nist_171: { title: 'NIST SP 800-171 Assessment Report',     subtitle: 'Controlled Unclassified Information Assessment',     classification: 'CUI',              shortName: 'NIST 171'  },
  soc2:     { title: 'SOC 2 Audit Readiness Report',          subtitle: 'Trust Services Criteria Assessment',                 classification: 'Confidential',     shortName: 'SOC 2'     },
  iso27001: { title: 'ISO 27001 Statement of Applicability',  subtitle: 'ISO/IEC 27001:2022 Annex A Control Assessment',     classification: 'Confidential',     shortName: 'ISO 27001' },
  hipaa:    { title: 'HIPAA Risk Analysis Report',            subtitle: 'HIPAA Security Rule Safeguard Assessment',           classification: 'PHI — Restricted', shortName: 'HIPAA'     },
  nist_csf: { title: 'NIST CSF 2.0 Assessment Report',       subtitle: 'Cybersecurity Framework Maturity Evaluation',        classification: 'Internal',         shortName: 'NIST CSF'  },
  pci_dss:  { title: 'PCI DSS v4.0 Compliance Report',       subtitle: 'Payment Card Industry Data Security Assessment',     classification: 'Confidential',     shortName: 'PCI DSS'   },
  fedramp:  { title: 'FedRAMP Moderate Assessment Report',    subtitle: 'NIST SP 800-53 Rev 5 Control Assessment',            classification: 'FedRAMP',          shortName: 'FedRAMP'   },
};

const PCT_SCORE_FNS = {
  soc2:     calculateSOC2Score,
  iso27001: calculateISO27001Score,
  hipaa:    calculateHIPAAScore,
  nist_csf: calculateNISTCSFScore,
  pci_dss:  calculatePCIDSSScore,
  fedramp:  calculateFedRAMPScore,
};

const CMMC_FRAMEWORKS = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171'];

function getMeta(framework) {
  return FRAMEWORK_META[framework] || {
    title: 'Compliance Assessment Report',
    subtitle: 'Control Assessment',
    classification: 'Confidential',
    shortName: framework?.toUpperCase() || 'FRAMEWORK',
  };
}

function computePctScore(framework, answers, questions) {
  const fn = PCT_SCORE_FNS[framework];
  if (fn) return fn(answers);
  const yes   = questions.filter(q => answers[q.id] === 'yes').length;
  const total = questions.length;
  return total > 0 ? Math.round((yes / total) * 100) : 0;
}

function computeDomainStats(questions, answers) {
  const map = {};
  questions.forEach(q => {
    if (!map[q.domain]) map[q.domain] = { id: q.domain, name: q.domain, met: 0, total: 0 };
    map[q.domain].total++;
    if (answers[q.id] === 'yes') map[q.domain].met++;
  });
  return Object.values(map).map(d => ({
    ...d,
    pct: d.total > 0 ? Math.round((d.met / d.total) * 100) : 0,
  }));
}

// ── Main SSP generator ────────────────────────────────────────────────────────
export function generateSSP(
  level,
  levelData,
  answers,
  questions,
  tasks,
  evidence,
  profile,
  framework = 'cmmc_l2',
  company   = {},
  narratives      = [],
  controlHistory  = [],
  sprsHistory     = [],
) {
  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W      = 210;
  const H      = 297;
  const M      = 14;   // margin
  const CW     = W - M * 2; // content width = 182mm
  const today  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const meta   = getMeta(framework);
  const isCmmc = CMMC_FRAMEWORKS.includes(framework);
  const isCui  = isCmmc || framework === 'fedramp';
  const orgName = company?.name || profile?.company_name || '[Organization Name]';

  // ── Colors ──────────────────────────────────────────────────────────────────
  const navy   = [15,  52,  96];
  const accent = [45,  125, 210];
  const green  = [29,  184, 122];
  const amber  = [245, 166, 35];
  const red    = [224, 82,  82];
  const white  = [255, 255, 255];
  const light  = [245, 247, 250];
  const gray   = [100, 100, 100];
  const dark   = [30,  30,  30];

  function statusColor(pct) { return pct >= 80 ? green : pct >= 50 ? amber : red; }
  function statusLabel(pct) { return pct >= 80 ? 'On Track' : pct >= 50 ? 'In Progress' : 'At Risk'; }

  function getNistRef(controlId) {
    if (!controlId) return '';
    const m = controlId.match(/(\d+\.\d+\.\d+)/);
    return m ? m[1] : '';
  }

  // Render a section title line below the global header area (y≥16)
  function sectionTitle(title) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    doc.text(title, M, 22);
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.5);
    doc.line(M, 24, W - M, 24);
  }

  // ── Derived data ─────────────────────────────────────────────────────────────
  const pctScore  = computePctScore(framework, answers, questions);
  const sprsScore = isCmmc ? calculateSPRSScore(answers) : null;
  const metCount  = questions.filter(q => answers[q.id] === 'yes').length;
  const totalQs   = questions.length;
  const gapCount  = questions.filter(q => answers[q.id] === 'no').length;
  const openTasks = (tasks || []).filter(t => !t.done);
  const domains   = isCmmc && levelData?.domains
    ? levelData.domains
    : computeDomainStats(questions, answers);

  const pageTracker = {};

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 1 — COVER
  // ════════════════════════════════════════════════════════════════════════════
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 72, 'F');
  doc.setFillColor(...accent);
  doc.rect(0, 72, W, 3, 'F');

  // CUI badge top-right
  if (isCui) {
    doc.setFillColor(...red);
    doc.roundedRect(W - M - 22, 7, 22, 9, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text('CUI', W - M - 11, 12.5, { align: 'center' });
  }

  doc.setTextColor(...white);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(meta.title, W / 2, 26, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(meta.subtitle, W / 2, 38, { align: 'center' });

  doc.setFontSize(8.5);
  doc.text(`Classification: ${meta.classification}`, W / 2, 50, { align: 'center' });

  // Org block
  doc.setTextColor(...dark);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(orgName, W / 2, 94, { align: 'center' });

  const cageCode = company?.cage_code || profile?.cage_code;
  if (cageCode) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text(`CAGE Code: ${cageCode}`, W / 2, 101, { align: 'center' });
  }

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(`Assessment Date: ${today}`, W / 2, cageCode ? 108 : 101, { align: 'center' });
  doc.text(`Document Version: ${company?.document_version || '1.0'}`, W / 2, cageCode ? 114 : 107, { align: 'center' });

  // Score box
  const scoreBoxTop = 124;
  doc.setFillColor(...light);
  doc.roundedRect(55, scoreBoxTop, 100, 52, 4, 4, 'F');
  doc.setFillColor(...accent);
  doc.roundedRect(55, scoreBoxTop, 100, 8, 4, 4, 'F');
  doc.rect(55, scoreBoxTop + 4, 100, 4, 'F');

  doc.setTextColor(...white);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text('OVERALL READINESS SCORE', W / 2, scoreBoxTop + 5.5, { align: 'center' });

  const bigScore = isCmmc ? String(sprsScore) : `${pctScore}%`;
  const bigColor = isCmmc
    ? (sprsScore >= 88 ? green : sprsScore >= 50 ? amber : red)
    : statusColor(pctScore);

  doc.setTextColor(...bigColor);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text(bigScore, W / 2, scoreBoxTop + 29, { align: 'center' });

  if (isCmmc) {
    doc.setTextColor(...gray);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text('out of 110', W / 2, scoreBoxTop + 37, { align: 'center' });
  }

  doc.setTextColor(...gray);
  doc.setFontSize(8);
  doc.text(
    `${metCount} of ${totalQs} controls implemented  ·  ${gapCount} gaps`,
    W / 2, isCmmc ? scoreBoxTop + 44 : scoreBoxTop + 38, { align: 'center' }
  );

  // Executive stats row
  const statItems = [
    { label: 'Controls Met',   value: String(metCount),              color: green  },
    { label: 'Open Gaps',      value: String(gapCount),              color: red    },
    { label: 'Open Tasks',     value: String(openTasks.length),      color: amber  },
    { label: 'Evidence Files', value: String((evidence || []).length), color: accent },
  ];
  const bw    = 40;
  const bGap  = 4;
  const bStart = (W - (statItems.length * bw + (statItems.length - 1) * bGap)) / 2;
  statItems.forEach((s, i) => {
    const bx = bStart + i * (bw + bGap);
    doc.setFillColor(...light);
    doc.roundedRect(bx, 184, bw, 22, 2, 2, 'F');
    doc.setTextColor(...s.color);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(s.value, bx + bw / 2, 194, { align: 'center' });
    doc.setTextColor(...gray);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(s.label, bx + bw / 2, 201, { align: 'center' });
  });

  // Cover footer
  doc.setFillColor(...navy);
  doc.rect(0, H - 20, W, 20, 'F');
  doc.setTextColor(...white);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('TheClearAudit — Compliance Platform', W / 2, H - 12, { align: 'center' });
  doc.text(`${meta.shortName}  ·  ${today}`, W / 2, H - 6, { align: 'center' });

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 2 — TABLE OF CONTENTS  (rendered last, after all pages are built)
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  const tocPage = doc.internal.getNumberOfPages(); // = 2

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 3 — CONFIDENTIALITY NOTICE
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Confidentiality Notice'] = doc.internal.getNumberOfPages();

  sectionTitle('Confidentiality Notice');

  const cuiNotice = [
    'This document contains Controlled Unclassified Information (CUI) as defined by Executive',
    'Order 13556 and 32 CFR Part 2002. It is to be handled and protected in accordance with',
    'applicable CUI safeguarding requirements.',
    '',
    '⚠  DISTRIBUTION: AUTHORIZED RECIPIENTS ONLY',
    '',
    'This System Security Plan (SSP) is an internal working document that describes the security',
    'controls implemented within the information system boundary and the policies and procedures',
    'in place to protect sensitive information. Unauthorized disclosure of this document could',
    'compromise the security posture of the organization and its compliance obligations.',
    '',
    'Recipients of this document are responsible for:',
    '    •  Maintaining the confidentiality of its contents',
    '    •  Not reproducing or distributing without written authorization',
    '    •  Reporting any unauthorized access or disclosure immediately',
    '    •  Destroying copies when no longer needed per CUI handling requirements',
    '',
    'This document may contain proprietary business information, personally identifiable',
    'information (PII), and technical data subject to export control regulations.',
    '',
    `Organization:  ${orgName}`,
    `Generated:     ${today}`,
    `Framework:     ${meta.shortName}`,
    `Classification: ${meta.classification}`,
  ];

  const confNotice = isCui ? cuiNotice : [
    'This document is classified as CONFIDENTIAL and is intended for authorized recipients only.',
    '',
    'This assessment report describes the current state of compliance controls and identifies',
    'areas requiring remediation. Contents should be protected in accordance with your',
    "organization's information classification and handling policies.",
    '',
    'Recipients are responsible for maintaining confidentiality, not distributing without',
    'authorization, and reporting any unauthorized access.',
    '',
    `Organization:  ${orgName}`,
    `Generated:     ${today}`,
    `Framework:     ${meta.shortName}`,
    `Classification: ${meta.classification}`,
  ];

  let cy = 30;
  confNotice.forEach(line => {
    const isAlert = line.startsWith('⚠');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', isAlert ? 'bold' : 'normal');
    doc.setTextColor(...(isAlert ? red : dark));
    doc.text(line, M, cy);
    cy += isAlert ? 7 : 5.5;
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 1 — SYSTEM DESCRIPTION
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 1: System Description'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 1: System Description');

  const sec1Rows = [
    ['Organization Name',     orgName],
    ['System Name',           company?.system_name           || 'Not Specified'],
    ['System Type',           company?.system_type           || 'Not Specified'],
    ['Operating Environment', company?.operating_environment || 'Not Specified'],
    ['CAGE Code',             cageCode                        || 'Not Set'],
    ['Assessment Date',       today],
    ['Document Version',      company?.document_version      || '1.0'],
    ['Classification',        meta.classification],
    ['Framework',             meta.shortName],
    ['Next Review Date',      company?.next_review_date      || 'Not Scheduled'],
  ];

  if (isCmmc) {
    sec1Rows.push(['CMMC Target Level',    `Level ${level}`]);
    sec1Rows.push(['SPRS Score',           `${sprsScore} out of 110`]);
  } else {
    sec1Rows.push(['Compliance Score',     `${pctScore}%`]);
  }
  sec1Rows.push(['Controls Implemented', `${metCount} of ${totalQs}`]);
  sec1Rows.push(['Open Gaps',            String(gapCount)]);

  autoTable(doc, {
    startY: 28,
    head: [['Field', 'Value']],
    body: sec1Rows,
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9.5 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 9.5, cellPadding: 3.5 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 72 }, 1: { cellWidth: 110 } },
    margin: { left: M, right: M },
  });

  if (company?.system_boundary) {
    const bY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    doc.text('System Boundary Description', M, bY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    const lines = doc.splitTextToSize(company.system_boundary, CW);
    doc.text(lines, M, bY + 6);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 2 — RESPONSIBLE PARTIES
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 2: Responsible Parties'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 2: Responsible Parties');

  const sec2Rows = [
    ['System Owner Name',      company?.system_owner_name    || 'Not Specified'],
    ['System Owner Title',     company?.system_owner_title   || 'Not Specified'],
    ['ISSO Name',              company?.isso_name            || 'Not Specified'],
    ['ISSO Email',             company?.isso_email           || 'Not Specified'],
    ['Authorizing Official',   company?.authorizing_official || 'Not Specified'],
    ['Contact Name',           profile?.contact_name         || 'Not Set'],
    ['Contact Phone',          profile?.phone                || 'Not Set'],
    ['Contact Address',        profile?.city
      ? `${profile.city}, ${profile.state || ''} ${profile.zip || ''}`.trim()
      : 'Not Set'],
    ['Reviewed By',            company?.reviewed_by          || 'Not Specified'],
    ['Approved By',            company?.approved_by          || 'Not Specified'],
  ];

  autoTable(doc, {
    startY: 28,
    head: [['Role', 'Name / Contact']],
    body: sec2Rows,
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9.5 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 9.5, cellPadding: 3.5 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 72 }, 1: { cellWidth: 110 } },
    margin: { left: M, right: M },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 3 — DOMAIN READINESS SUMMARY
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 3: Domain Readiness Summary'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 3: Domain Readiness Summary');

  autoTable(doc, {
    startY: 28,
    head: [['Domain / Category', 'ID', 'Score', 'Controls', 'Status']],
    body: domains.map(d => [d.name, d.id, `${d.pct}%`, `${d.met} / ${d.total}`, statusLabel(d.pct)]),
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9.5 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 9.5, cellPadding: 3.5 },
    columnStyles: { 0: { cellWidth: 72 }, 1: { cellWidth: 18 }, 2: { cellWidth: 22 }, 3: { cellWidth: 28 }, 4: { cellWidth: 42 } },
    margin: { left: M, right: M },
    didDrawCell: (data) => {
      if (data.column.index === 4 && data.section === 'body') {
        const val     = data.cell.raw;
        const color   = val === 'On Track' ? green : val === 'In Progress' ? amber : red;
        const bgColor = val === 'On Track' ? [230,247,238] : val === 'In Progress' ? [255,248,230] : [252,235,235];
        doc.setFillColor(...bgColor);
        doc.rect(data.cell.x+1, data.cell.y+1, data.cell.width-2, data.cell.height-2, 'F');
        doc.setTextColor(...color);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.text(val, data.cell.x + data.cell.width/2, data.cell.y + data.cell.height/2 + 1, { align: 'center', baseline: 'middle' });
        doc.setTextColor(...dark);
        doc.setFont('helvetica', 'normal');
      }
    },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 4 — ASSESSMENT RESULTS
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 4: Assessment Results'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 4: Assessment Results');

  const assessRows = questions.map(q => {
    const ans       = answers[q.id] || 'Not Answered';
    const label     = ans === 'yes' ? 'Implemented' : ans === 'partial' ? 'Partial' : ans === 'no' ? 'Not Implemented' : 'Not Answered';
    const req       = q.text.length > 62 ? q.text.substring(0, 62) + '…' : q.text;
    const narrative = (narratives || []).find(n => n.control_id === q.id)?.narrative || '';
    const narShort  = narrative.length > 45 ? narrative.substring(0, 45) + '…' : narrative;

    return isCmmc
      ? [q.id, getNistRef(q.id), q.domain, req, narShort, label]
      : [q.id, q.domain, req, narShort, label];
  });

  const assessHead = isCmmc
    ? [['Control ID', 'NIST Ref', 'Domain', 'Requirement', 'Narrative', 'Status']]
    : [['Control ID', 'Domain',   'Requirement',             'Narrative', 'Status']];

  // Column widths totaling 182mm
  const assessCols = isCmmc
    ? { 0: { cellWidth: 28 }, 1: { cellWidth: 18 }, 2: { cellWidth: 14 }, 3: { cellWidth: 58 }, 4: { cellWidth: 42 }, 5: { cellWidth: 22 } }
    : { 0: { cellWidth: 28 }, 1: { cellWidth: 16 }, 2: { cellWidth: 66 }, 3: { cellWidth: 48 }, 4: { cellWidth: 24 } };

  const statusCol = isCmmc ? 5 : 4;

  autoTable(doc, {
    startY: 28,
    head: assessHead,
    body: assessRows,
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 7.5, cellPadding: 2.5 },
    columnStyles: assessCols,
    margin: { left: M, right: M },
    didDrawCell: (data) => {
      if (data.column.index === statusCol && data.section === 'body') {
        const val     = data.cell.raw;
        if (val === 'Not Answered') return;
        const color   = val === 'Implemented' ? green : val === 'Partial' ? amber : red;
        const bgColor = val === 'Implemented' ? [230,247,238] : val === 'Partial' ? [255,248,230] : [252,235,235];
        doc.setFillColor(...bgColor);
        doc.rect(data.cell.x+1, data.cell.y+1, data.cell.width-2, data.cell.height-2, 'F');
        doc.setTextColor(...color);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(val, data.cell.x + data.cell.width/2, data.cell.y + data.cell.height/2 + 1, { align: 'center', baseline: 'middle' });
        doc.setTextColor(...dark);
        doc.setFont('helvetica', 'normal');
      }
    },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 5 — PLAN OF ACTION AND MILESTONES (POA&M)
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 5: Plan of Action and Milestones'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 5: Plan of Action and Milestones (POA\u0026M)');

  autoTable(doc, {
    startY: 28,
    head: [['POA&M ID', 'Task', 'Domain', 'Priority', 'Owner', 'Due Date', 'Status']],
    body: openTasks.map((t, i) => [
      `POA&M-${String(i + 1).padStart(3, '0')}`,
      t.name || t.title || '',
      t.domain || '',
      (t.priority || 'low').toUpperCase().replace('MEDIUM', 'MED'),
      t.owner || '',
      t.due   || '',
      'Open',
    ]),
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 8, cellPadding: 3 },
    // 22+52+18+18+28+22+22 = 182
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 52 }, 2: { cellWidth: 18 }, 3: { cellWidth: 18 }, 4: { cellWidth: 28 }, 5: { cellWidth: 22 }, 6: { cellWidth: 22 } },
    margin: { left: M, right: M },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 6 — EVIDENCE INVENTORY
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 6: Evidence Inventory'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 6: Evidence Inventory');

  const evidenceRows = (evidence || []).map(e => {
    let expiryLabel = '';
    if (e.expires_at) {
      const daysLeft = Math.floor((new Date(e.expires_at) - new Date()) / 86400000);
      expiryLabel = daysLeft < 0
        ? `EXPIRED ${e.expires_at}`
        : daysLeft <= 30
          ? `SOON ${e.expires_at}`
          : e.expires_at;
    }
    return [e.name || '', e.control || '', e.date || '', expiryLabel, e.status || 'Draft'];
  });

  autoTable(doc, {
    startY: 28,
    head: [['File Name', 'Mapped Control', 'Date Added', 'Expiry', 'Status']],
    body: evidenceRows,
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 8.5, cellPadding: 3 },
    // 62+38+28+32+22 = 182
    columnStyles: { 0: { cellWidth: 62 }, 1: { cellWidth: 38 }, 2: { cellWidth: 28 }, 3: { cellWidth: 32 }, 4: { cellWidth: 22 } },
    margin: { left: M, right: M },
    didDrawCell: (data) => {
      if (data.column.index === 3 && data.section === 'body') {
        const val = data.cell.raw;
        if (!val) return;
        const isExpired = val.startsWith('EXPIRED');
        const isSoon    = val.startsWith('SOON');
        if (isExpired || isSoon) {
          doc.setFillColor(...(isExpired ? [252,235,235] : [255,248,230]));
          doc.rect(data.cell.x+1, data.cell.y+1, data.cell.width-2, data.cell.height-2, 'F');
          doc.setTextColor(...(isExpired ? red : amber));
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          const displayVal = isExpired ? val.replace('EXPIRED ', '') : val.replace('SOON ', '');
          doc.text(
            isExpired ? `EXPIRED\n${displayVal}` : `SOON\n${displayVal}`,
            data.cell.x + 2, data.cell.y + data.cell.height / 2 - 1,
            { baseline: 'middle' }
          );
          doc.setTextColor(...dark);
          doc.setFont('helvetica', 'normal');
        }
      }
    },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 7 — AUDIT TRAIL
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  pageTracker['Section 7: Audit Trail'] = doc.internal.getNumberOfPages();
  sectionTitle('Section 7: Audit Trail');

  const histRows = (controlHistory || []).slice(0, 50).map(h => [
    h.control_id || '',
    h.framework  || '',
    h.old_status ? h.old_status.charAt(0).toUpperCase() + h.old_status.slice(1) : '—',
    h.new_status ? h.new_status.charAt(0).toUpperCase() + h.new_status.slice(1) : '—',
    h.changed_by_name || '',
    h.created_at
      ? new Date(h.created_at).toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
      : '',
  ]);

  autoTable(doc, {
    startY: 28,
    head: [['Control ID', 'Framework', 'From', 'To', 'Changed By', 'Date']],
    body: histRows.length > 0 ? histRows : [['—', '—', '—', '—', '—', 'No audit history recorded']],
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 8.5, cellPadding: 3 },
    // 30+26+26+26+42+32 = 182
    columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 26 }, 2: { cellWidth: 26 }, 3: { cellWidth: 26 }, 4: { cellWidth: 42 }, 5: { cellWidth: 32 } },
    margin: { left: M, right: M },
  });

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 8 — SPRS SCORE HISTORY  (CMMC / NIST only)
  // ════════════════════════════════════════════════════════════════════════════
  let sec8Page = null;
  if (isCmmc && (sprsHistory || []).length > 0) {
    doc.addPage();
    sec8Page = doc.internal.getNumberOfPages();
    pageTracker['Section 8: SPRS Score History'] = sec8Page;
    sectionTitle('Section 8: SPRS Score History');

    autoTable(doc, {
      startY: 28,
      head: [['Date', 'SPRS Score', 'Out Of', 'Notes']],
      body: (sprsHistory || []).map(s => [
        s.recorded_at
          ? new Date(s.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : '',
        String(s.score ?? ''),
        '110',
        s.notes || '',
      ]),
      headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 10 },
      alternateRowStyles: { fillColor: light },
      styles: { fontSize: 9.5, cellPadding: 3.5 },
      // 42+28+20+92 = 182
      columnStyles: { 0: { cellWidth: 42 }, 1: { cellWidth: 28 }, 2: { cellWidth: 20 }, 3: { cellWidth: 92 } },
      margin: { left: M, right: M },
    });
  }

  // ════════════════════════════════════════════════════════════════════════════
  // FINAL PAGE — SIGNATURE BLOCK
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  const sigPage = doc.internal.getNumberOfPages();
  pageTracker['Signature Block'] = sigPage;
  sectionTitle('Document Approval and Signature Block');

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  const sigIntro = 'The undersigned acknowledge they have reviewed this document and agree with the information provided herein.';
  doc.text(sigIntro, M, 30, { maxWidth: CW });

  const sigBlocks = [
    { role: 'System Owner',                              name: company?.system_owner_name    || '', title: company?.system_owner_title || '' },
    { role: 'Information System Security Officer (ISSO)', name: company?.isso_name           || '', title: 'ISSO' },
    { role: 'Authorizing Official',                      name: company?.authorizing_official || '', title: '' },
    { role: 'Reviewed By',                               name: company?.reviewed_by          || '', title: '' },
    { role: 'Approved By',                               name: company?.approved_by          || '', title: '' },
  ];

  let sy = 42;
  sigBlocks.forEach(sig => {
    doc.setFillColor(...light);
    doc.roundedRect(M, sy, CW, 28, 2, 2, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.roundedRect(M, sy, CW, 28, 2, 2, 'S');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accent);
    doc.text(sig.role.toUpperCase(), M + 3, sy + 7);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...dark);
    doc.text(sig.name || '________________________________', M + 3, sy + 15);

    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text('Signature: ________________________________', M + 3, sy + 23);
    doc.text('Date: __________________', W - M - 55, sy + 23);

    sy += 32;
  });

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER TOC  (go back to page 2, now that all pages are built)
  // ════════════════════════════════════════════════════════════════════════════
  doc.setPage(tocPage);
  sectionTitle('Table of Contents');

  const tocEntries = [
    ['Confidentiality Notice',              pageTracker['Confidentiality Notice']],
    ['Section 1: System Description',       pageTracker['Section 1: System Description']],
    ['Section 2: Responsible Parties',      pageTracker['Section 2: Responsible Parties']],
    ['Section 3: Domain Readiness Summary', pageTracker['Section 3: Domain Readiness Summary']],
    ['Section 4: Assessment Results',       pageTracker['Section 4: Assessment Results']],
    ['Section 5: Plan of Action and Milestones', pageTracker['Section 5: Plan of Action and Milestones']],
    ['Section 6: Evidence Inventory',       pageTracker['Section 6: Evidence Inventory']],
    ['Section 7: Audit Trail',              pageTracker['Section 7: Audit Trail']],
  ];
  if (sec8Page) tocEntries.push(['Section 8: SPRS Score History', sec8Page]);
  tocEntries.push(['Signature Block', sigPage]);

  let ty = 30;
  tocEntries.forEach(entry => {
    const label   = entry[0];
    const pageNum = String(entry[1]);
    const isMain  = label.startsWith('Section');

    doc.setFontSize(isMain ? 10 : 9.5);
    doc.setFont('helvetica', isMain ? 'bold' : 'normal');
    doc.setTextColor(...dark);
    doc.text(label, M + (isMain ? 0 : 4), ty);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text(pageNum, W - M, ty, { align: 'right' });

    // Leader dots
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.15);
    const lx1 = M + doc.getTextWidth(label) + (isMain ? 3 : 7);
    const lx2 = W - M - doc.getTextWidth(pageNum) - 2;
    if (lx2 > lx1 + 4) {
      for (let dx = lx1; dx < lx2; dx += 3) {
        doc.circle(dx, ty - 0.8, 0.3, 'F');
      }
    }

    ty += isMain ? 9 : 8;
  });

  // ════════════════════════════════════════════════════════════════════════════
  // GLOBAL HEADERS + FOOTERS  (all pages)
  // ════════════════════════════════════════════════════════════════════════════
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Header (skip cover page)
    if (i > 1) {
      doc.setFillColor(...navy);
      doc.rect(0, 0, W, 13, 'F');
      doc.setTextColor(...white);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.text(orgName, M, 8.5);
      doc.setFont('helvetica', 'bold');
      doc.text(meta.classification, W - M, 8.5, { align: 'right' });
    }

    // Footer (all pages)
    doc.setFillColor(...light);
    doc.rect(0, H - 11, W, 11, 'F');
    doc.setDrawColor(215, 220, 228);
    doc.setLineWidth(0.25);
    doc.line(0, H - 11, W, H - 11);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`TheClearAudit  ·  ${meta.title}  ·  ${today}`, M, H - 4.5);
    doc.text(`Page ${i} of ${totalPages}`, W - M, H - 4.5, { align: 'right' });
  }

  // CUI watermark on content pages (skip cover, TOC, sig block)
  if (isCui) {
    for (let i = 3; i < sigPage; i++) {
      doc.setPage(i);
      doc.setTextColor(238, 238, 240);
      doc.setFontSize(80);
      doc.setFont('helvetica', 'bold');
      doc.text('CUI', W / 2, H / 2, { align: 'center', angle: 45 });
    }
  }

  const fwSlug = framework || 'report';
  doc.save(`TheClearAudit_${fwSlug.toUpperCase()}_SSP_${Date.now()}.pdf`);
}

// ── Standalone POA&M export ───────────────────────────────────────────────────
export function generatePOAM(tasks, level, framework = 'cmmc_l2', profile) {
  const doc       = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const today     = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const meta      = getMeta(framework);
  const isCmmc    = CMMC_FRAMEWORKS.includes(framework);
  const companyName = profile?.company_name || '[Organization Name]';

  const navy  = [15, 52, 96];
  const white = [255, 255, 255];
  const light = [245, 247, 250];
  const gray  = [100, 100, 100];
  const dark  = [30, 30, 30];

  const poamTitle = isCmmc
    ? `Plan of Action & Milestones — CMMC Level ${level}`
    : `Plan of Action & Milestones — ${meta.shortName}`;

  // Header
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 22, 'F');
  doc.setTextColor(...white);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(poamTitle, 14, 11);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(companyName, 14, 19);

  doc.setTextColor(...dark);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${meta.subtitle} — Generated: ${today}`, 14, 30);

  const openTasks = (tasks || []).filter(t => !t.done);
  const doneTasks = (tasks || []).filter(t => t.done);

  function normP(p) { return p === 'med' ? 'medium' : (p || 'low'); }
  const highCount = openTasks.filter(t => normP(t.priority) === 'high').length;
  const medCount  = openTasks.filter(t => normP(t.priority) === 'medium').length;
  const lowCount  = openTasks.filter(t => normP(t.priority) === 'low').length;

  // Summary boxes
  const boxes = [
    { label: 'Open Items',    value: openTasks.length, color: [224, 82, 82]   },
    { label: 'Completed',     value: doneTasks.length,  color: [29, 184, 122] },
    { label: 'High Priority', value: highCount,         color: [224, 82, 82]  },
    { label: 'Med Priority',  value: medCount,          color: [245, 166, 35] },
    { label: 'Low Priority',  value: lowCount,          color: [45, 125, 210] },
  ];

  boxes.forEach((box, i) => {
    const x = 14 + i * 38;
    doc.setFillColor(...light);
    doc.roundedRect(x, 32, 34, 22, 2, 2, 'F');
    doc.setTextColor(...box.color);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(box.value), x + 17, 45, { align: 'center' });
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(box.label, x + 17, 51, { align: 'center' });
  });

  // Open tasks
  doc.setTextColor(...dark);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Open Action Items', 14, 66);

  autoTable(doc, {
    startY: 70,
    head: [['Task', 'Domain', 'Priority', 'Owner', 'Due Date']],
    body: openTasks.map(t => [
      t.name || t.title || '',
      t.domain || '',
      normP(t.priority).toUpperCase(),
      t.owner || '',
      t.due   || '',
    ]),
    headStyles: { fillColor: navy, textColor: white, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 75 }, 1: { cellWidth: 18 }, 2: { cellWidth: 22 }, 3: { cellWidth: 35 }, 4: { cellWidth: 30 } },
  });

  // Completed tasks
  const completedY = doc.lastAutoTable.finalY + 10;
  doc.setTextColor(...dark);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Completed Items', 14, completedY);

  autoTable(doc, {
    startY: completedY + 4,
    head: [['Task', 'Domain', 'Priority', 'Owner', 'Due Date']],
    body: doneTasks.map(t => [
      t.name || t.title || '',
      t.domain || '',
      normP(t.priority).toUpperCase(),
      t.owner || '',
      t.due   || '',
    ]),
    headStyles: { fillColor: [29, 184, 122], textColor: white, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: light },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 75 }, 1: { cellWidth: 18 }, 2: { cellWidth: 22 }, 3: { cellWidth: 35 }, 4: { cellWidth: 30 } },
  });

  // Page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(...light);
    doc.rect(0, 285, pageWidth, 12, 'F');
    doc.setTextColor(...gray);
    doc.setFontSize(8);
    doc.text(`TheClearAudit — POA&M — ${today}`, 14, 292);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, 292, { align: 'right' });
  }

  const fwSlug = framework || 'report';
  doc.save(`TheClearAudit_POAM_${fwSlug.toUpperCase()}_${Date.now()}.pdf`);
}
